import { doc, getDoc, updateDoc } from "firebase/firestore";
import { HelpCircle, Mail, User } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useUser } from "../contexts/UserContext";
import { db } from "../firebaseConfig";

const Settings = ({ isFreeAccess = false }) => {
	const { user } = useUser();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [emailPrefs, setEmailPrefs] = useState([]);
	const [savingPrefs, setSavingPrefs] = useState(false);

	useEffect(() => {
		const fetchPreferences = async () => {
			if (!user) return;

			const userRef = doc(db, "Users", user.uid);
			const userDoc = await getDoc(userRef);
			if (userDoc.exists()) {
				const userData = userDoc.data();
				setEmailPrefs(userData.emailPreferences || []);
			}
		};

		fetchPreferences();
	}, [user]);

	const handleEmailPreferences = async (prefs) => {
		setSavingPrefs(true);
		try {
			const userRef = doc(db, "Users", user.uid);
			await updateDoc(userRef, {
				emailPreferences: prefs,
				emailPrefsSet: true,
			});
			setEmailPrefs(prefs);
		} catch (err) {
			console.error("Error updating email preferences:", err);
			setError("Failed to update email preferences");
		} finally {
			setSavingPrefs(false);
		}
	};

	const handleManageBilling = async () => {
		setLoading(true);
		setError(null);

		try {
			const response = await fetch(
				"/.netlify/functions/create-portal-session",
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						userId: user.uid,
					}),
				}
			);

			if (!response.ok) {
				throw new Error("Failed to create portal session");
			}

			const data = await response.json();
			if (data.url) {
				window.location.href = data.url;
			} else {
				throw new Error("No portal URL received");
			}
		} catch (err) {
			console.error("Error accessing billing portal:", err);
			setError(
				"Unable to access billing portal. Please try again later."
			);
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
			<div className="w-full max-w-4xl mx-auto p-6 backdrop-blur-lg rounded-3xl shadow-xl overflow-hidden bg-white bg-opacity-90">
				<h1 className="text-2xl font-light text-gray-800 mb-6 text-center">
					Settings
				</h1>

				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					{/* Profile Section */}
					<div className="bg-white rounded-lg p-4">
						<div className="flex items-center gap-3 mb-4">
							<User className="text-blue-600" size={24} />
							<h2 className="text-lg font-light text-gray-800">
								Profile
							</h2>
						</div>
						<div className="space-y-4">
							<div>
								<label className="block text-sm font-medium text-gray-700">
									Email
								</label>
								<p className="mt-1 text-gray-800">
									{user?.email}
								</p>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700">
									Name
								</label>
								<p className="mt-1 text-gray-800">
									{user?.displayName || "Not set"}
								</p>
							</div>
						</div>
					</div>

					{/* Email Preferences Section */}
					<div className="bg-white rounded-lg p-4">
						<div className="flex items-center gap-3 mb-3">
							<Mail className="text-blue-600" size={20} />
							<h2 className="text-lg font-light text-gray-800">
								Email Preferences
							</h2>
						</div>
						<div className="space-y-2">
							{[
								{
									id: "progress",
									title: "Progress Updates",
									description:
										"Get personalized summaries of your milestones and reminders to continue your growth.",
								},
								{
									id: "prompts",
									title: "Reflection Prompts",
									description:
										"Inspiration for your session topics, tailored just for your personal growth.",
								},
								{
									id: "educational",
									title: "Educational Content",
									description:
										"Receive expert tips, wellness strategies, and curated mental health resources.",
								},
							].map((pref) => (
								<div
									key={pref.id}
									className="flex items-start space-x-3">
									<input
										type="checkbox"
										id={`settings-${pref.id}`}
										checked={emailPrefs.includes(pref.id)}
										onChange={(e) => {
											const newPrefs = e.target.checked
												? [...emailPrefs, pref.id]
												: emailPrefs.filter(
														(p) => p !== pref.id
												  );
											handleEmailPreferences(newPrefs);
										}}
										className="mt-1"
										disabled={savingPrefs}
									/>
									<label
										htmlFor={`settings-${pref.id}`}
										className="flex flex-col cursor-pointer">
										<span className="font-medium">
											{pref.title}
										</span>
										<span className="text-sm text-gray-500">
											{pref.description}
										</span>
									</label>
								</div>
							))}
							{savingPrefs && (
								<div className="text-sm text-blue-600">
									Saving preferences...
								</div>
							)}
						</div>
					</div>

					{/* Contact Us Section */}
					<div className="bg-white rounded-lg p-4">
						<div className="flex items-center gap-3 mb-4">
							<HelpCircle className="text-blue-600" size={24} />
							<h2 className="text-lg font-light text-gray-800">
								Need Help?
							</h2>
						</div>
						<p className="text-gray-600">
							Have questions or need assistance?{" "}
							<a
								href="mailto:aniketdas001@gmail.com,krflame@gmail.com"
								target="_blank"
								rel="noopener noreferrer"
								className="text-blue-600 hover:text-blue-800">
								Contact our support team
							</a>
						</p>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Settings;
